
// overlayToolbar_target_ovs_v4.js
// Fix: getInfoAction is now a class method and is used in BOTH syncFromSelection and on-change.
// Stories are stored in overlay.actions as {type:"info", content:<infoId>} and mirrored to overlay.storyId.
// Also includes target.ovs add/delete UI.

export class OverlayToolbar {
  constructor({ overlaysM, editor, data }) {
    this.overlaysM = overlaysM;
    this.editor = editor;
    this.data = data;

    this.root = null;

    this.selOverlay = null;
    this.infoSel = null;

    this.addTargetSel = null;
    this.btnAddTarget = null;
    this.targetsList = null;
    this.targetLayerSel = null;

    // index infos by id (from data.stories[].infos[])
    this.infoById = new Map();
    const stories = Array.isArray(data?.stories) ? data.stories : [];
    for (const st of stories) {
      for (const inf of (st?.infos || [])) {
        if (inf?.id) this.infoById.set(inf.id, inf);
      }
    }
  }

  // ---- story/info action helpers ----
  _getInfoAction(ov) {
    const acts = Array.isArray(ov?.actions) ? ov.actions : [];
    return acts.find(a => a && a.type === "info") || null;
  }

  _ensureInfoAction(ov, infoId) {
    if (!Array.isArray(ov.actions)) ov.actions = [];
    let act = this._getInfoAction(ov);
    if (!act) {
      act = {
        id: Math.random().toString(36).slice(2, 10),
        type: "info",
        content: infoId,
        style: { position: [0, 0] }
      };
      // put it first so it's easy to find
      ov.actions.unshift(act);
    } else {
      act.content = infoId;
    }
  }

  _removeInfoActions(ov) {
    if (!Array.isArray(ov.actions)) return;
    ov.actions = ov.actions.filter(a => !(a && a.type === "info"));
  }

  // ---- target helper ----
  _ensureTarget(ov) {
    if (!ov.target || typeof ov.target !== "object") ov.target = { ovs: [], infolayerindex: 0 };
    if (!Array.isArray(ov.target.ovs)) ov.target.ovs = [];
    if (!Number.isFinite(ov.target.infolayerindex)) ov.target.infolayerindex = 0;
  }

  mount(parent) {
    this.root = document.createElement("div");
    this.root.className = "ovbar";

    const style = document.createElement("style");
    style.textContent = `
      .ovbar{
        position:absolute; top:12px; right:12px; z-index:99999;
        background:rgba(0,0,0,.60); padding:10px; border-radius:10px;
        color:#fff; font:14px/1.2 system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
        min-width:360px; max-width:460px
      }
      .ovbar .row{display:flex; gap:8px; align-items:center; margin-top:8px}
      .ovbar .title{font-weight:700; margin-bottom:4px}
      .ovbar .small{opacity:.85; font-size:12px}
      .ovbar select{
        flex:1; padding:6px; border-radius:8px;
        border:1px solid rgba(255,255,255,.22);
        background:rgba(0,0,0,.35); color:#fff
      }
      .ovbar button{
        padding:6px 10px; border-radius:8px;
        border:1px solid rgba(255,255,255,.22);
        background:rgba(255,255,255,.10); color:#fff; cursor:pointer
      }
      .ovbar button:disabled{opacity:.5; cursor:default}
      .ovbar .card{
        border:1px solid rgba(255,255,255,.18);
        border-radius:10px; padding:8px; background:rgba(255,255,255,.06)
      }
      .ovbar .targets{display:flex; flex-direction:column; gap:6px; margin-top:6px}
      .ovbar .titem{display:flex; gap:8px; align-items:center}
      .ovbar .tname{flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap}
      .ovbar .danger{
        background:rgba(255,0,0,.18);
        border-color:rgba(255,0,0,.30)
      }
      .ovbar .hint{opacity:.75; font-size:12px; margin-top:6px}
    `;
    document.head.appendChild(style);
    parent.appendChild(this.root);

    const overlayLabel = (o) => (o?.name ? `${o.name} (${o.id})` : o?.id || "");

    const getSelectedOverlayId = () => {
      const s = this.editor?.getSelection?.();
      return s?.overlayId || this.selOverlay?.value || null;
    };

    const getSelectedOverlay = () => {
      const id = getSelectedOverlayId();
      return id ? this.overlaysM.getOverlayById(id) : null;
    };

    // ---------- UI ----------
    const head = document.createElement("div");
    head.className = "title";
    head.textContent = "Overlay";
    this.root.appendChild(head);

    // overlay selector
    const rowSel = document.createElement("div");
    rowSel.className = "row";
    this.selOverlay = document.createElement("select");
    rowSel.appendChild(this.selOverlay);
    this.root.appendChild(rowSel);

    // info selector (stored in actions)
    const infoWrap = document.createElement("div");
    infoWrap.className = "card";
    const infoLbl = document.createElement("div");
    infoLbl.className = "small";
    infoLbl.textContent = "Story/Info (actions: type=\"info\", content=id)";
    infoWrap.appendChild(infoLbl);

    const rowInfo = document.createElement("div");
    rowInfo.className = "row";
    this.infoSel = document.createElement("select");
    rowInfo.appendChild(this.infoSel);
    infoWrap.appendChild(rowInfo);
    this.root.appendChild(infoWrap);

    // targets editor
    const targetsWrap = document.createElement("div");
    targetsWrap.className = "card";
    const targetsLbl = document.createElement("div");
    targetsLbl.className = "small";
    targetsLbl.textContent = "Targets (overlay.target.ovs)";
    targetsWrap.appendChild(targetsLbl);

    const rowAdd = document.createElement("div");
    rowAdd.className = "row";
    this.addTargetSel = document.createElement("select");
    this.btnAddTarget = document.createElement("button");
    this.btnAddTarget.textContent = "Add";
    rowAdd.appendChild(this.addTargetSel);
    rowAdd.appendChild(this.btnAddTarget);
    targetsWrap.appendChild(rowAdd);

    this.targetsList = document.createElement("div");
    this.targetsList.className = "targets";
    targetsWrap.appendChild(this.targetsList);

    const hint = document.createElement("div");
    hint.className = "hint";
    hint.textContent = "Add overlay IDs to jump to them. Delete removes from target.ovs.";
    targetsWrap.appendChild(hint);

    this.root.appendChild(targetsWrap);

    // target layer index selector
    const rowLayer = document.createElement("div");
    rowLayer.className = "row";
    this.targetLayerSel = document.createElement("select");
    rowLayer.appendChild(this.targetLayerSel);
    this.root.appendChild(rowLayer);

    // ---------- list builders ----------
    const rebuildOverlayList = () => {
      const current = this.selOverlay.value;
      this.selOverlay.innerHTML = "";
      for (const o of (this.overlaysM.overlays || [])) {
        const opt = document.createElement("option");
        opt.value = o.id;
        opt.textContent = o.name || o.id;
        this.selOverlay.appendChild(opt);
      }
      if (current) this.selOverlay.value = current;
    };

    const rebuildInfoList = () => {
      const current = this.infoSel.value;
      this.infoSel.innerHTML = "";

      if (this.infoById.size === 0) {
        const opt = document.createElement("option");
        opt.value = "";
        opt.textContent = "(no infos found in data.stories[].infos[])";
        this.infoSel.appendChild(opt);
        this.infoSel.disabled = true;
        return;
      }
      this.infoSel.disabled = false;

      const none = document.createElement("option");
      none.value = "";
      none.textContent = "(no info)";
      this.infoSel.appendChild(none);

      for (const [id, inf] of this.infoById.entries()) {
        const label = inf?.lngs?.at?.title || inf?.lngs?.gb?.title || inf?.lngs?.it?.title || id;
        const opt = document.createElement("option");
        opt.value = id;
        opt.textContent = `${label} (${id})`;
        this.infoSel.appendChild(opt);
      }
      this.infoSel.value = current || "";
    };

    const rebuildAddTargetList = () => {
      const current = this.addTargetSel.value;
      const ov = getSelectedOverlay();
      const selfId = ov?.id;

      this.addTargetSel.innerHTML = "";
      for (const o of (this.overlaysM.overlays || [])) {
        if (o.id === selfId) continue;
        const opt = document.createElement("option");
        opt.value = o.id;
        opt.textContent = overlayLabel(o);
        this.addTargetSel.appendChild(opt);
      }
      if (current) this.addTargetSel.value = current;
      this.btnAddTarget.disabled = (this.addTargetSel.options.length === 0);
    };

    const rebuildTargetLayerList = () => {
      const n = this.data?.page?.infoLayers?.length || 0;
      const current = this.targetLayerSel.value;

      this.targetLayerSel.innerHTML = "";
      for (let i = 0; i < n; i++) {
        const opt = document.createElement("option");
        opt.value = String(i);
        opt.textContent = `target layer index: ${i}`;
        this.targetLayerSel.appendChild(opt);
      }
      if (current) this.targetLayerSel.value = current;
    };

    const renderTargets = () => {
      const ov = getSelectedOverlay();
      this.targetsList.innerHTML = "";
      if (!ov) return;

      this._ensureTarget(ov);
      const ids = ov.target.ovs;

      if (!ids.length) {
        const empty = document.createElement("div");
        empty.className = "small";
        empty.textContent = "(no targets)";
        this.targetsList.appendChild(empty);
        return;
      }

      for (const id of ids) {
        const tOv = this.overlaysM.getOverlayById(id);
        const row = document.createElement("div");
        row.className = "titem";

        const name = document.createElement("div");
        name.className = "tname";
        name.textContent = tOv ? overlayLabel(tOv) : id;
        row.appendChild(name);

        const del = document.createElement("button");
        del.className = "danger";
        del.textContent = "Delete";
        del.addEventListener("click", () => {
          const cur = getSelectedOverlay();
          if (!cur) return;
          this._ensureTarget(cur);
          cur.target.ovs = cur.target.ovs.filter(x => x !== id);
          this.syncFromSelection(); // re-render
        });
        row.appendChild(del);

        this.targetsList.appendChild(row);
      }
    };

    // ---------- wire events ----------
    this.selOverlay.addEventListener("change", () => {
      rebuildAddTargetList();
      this.syncFromSelection();
    });

    this.infoSel.addEventListener("change", () => {
      const ov = getSelectedOverlay();
      if (!ov) return;

      const infoId = this.infoSel.value || "";
      if (infoId) {
        this._ensureInfoAction(ov, infoId);   // <-- calls _getInfoAction internally
        ov.storyId = infoId;                  // mirror
      } else {
        this._removeInfoActions(ov);
        ov.storyId = "";
      }
      // no need to dispatch change; just sync UI
      this.syncFromSelection();
    });

    this.btnAddTarget.addEventListener("click", () => {
      const ov = getSelectedOverlay();
      if (!ov) return;
      this._ensureTarget(ov);

      const id = this.addTargetSel.value;
      if (!id) return;
      if (!ov.target.ovs.includes(id) && id !== ov.id) ov.target.ovs.push(id);

      this.syncFromSelection();
    });

    this.targetLayerSel.addEventListener("change", () => {
      const ov = getSelectedOverlay();
      if (!ov) return;
      this._ensureTarget(ov);
      ov.target.infolayerindex = parseInt(this.targetLayerSel.value, 10) || 0;
    });

    // ---------- initial build ----------
    rebuildOverlayList();
    rebuildInfoList();
    rebuildTargetLayerList();
    rebuildAddTargetList();
    this.syncFromSelection();

    // follow editor selection changes (if no events are emitted)
    this._selTimer = setInterval(() => {
      const id = getSelectedOverlayId();
      if (id && this.selOverlay && this.selOverlay.value !== id) {
        this.selOverlay.value = id;
        rebuildAddTargetList();
        this.syncFromSelection();
      }
    }, 200);

    // expose for debugging if you want (optional)
    // window.__ovbar = this;
  }

  syncFromSelection() {
    if (!this.root) return;

    const s = this.editor?.getSelection?.();
    const selectedId = s?.overlayId || this.selOverlay?.value;
    const ov = selectedId ? this.overlaysM.getOverlayById(selectedId) : null;
    if (!ov) return;

    // overlay dropdown
    if (this.selOverlay) this.selOverlay.value = ov.id;

    // derive infoId from info-action
    const act = this._getInfoAction(ov); // <-- now ALWAYS called on selection sync
    const infoId = (act && typeof act.content === "string") ? act.content : "";

    // mirror to overlay.storyId (requested)
    ov.storyId = infoId || "";

    // set info selector
    if (this.infoSel) this.infoSel.value = infoId || "";

    // targets + layer
    this._ensureTarget(ov);

    // refresh add-target list so it won't offer self id
    if (this.addTargetSel && this.btnAddTarget) {
      const selfId = ov.id;
      const cur = this.addTargetSel.value;
      this.addTargetSel.innerHTML = "";
      for (const o of (this.overlaysM.overlays || [])) {
        if (o.id === selfId) continue;
        const opt = document.createElement("option");
        opt.value = o.id;
        opt.textContent = (o?.name ? `${o.name} (${o.id})` : o.id);
        this.addTargetSel.appendChild(opt);
      }
      if (cur) this.addTargetSel.value = cur;
      this.btnAddTarget.disabled = (this.addTargetSel.options.length === 0);
    }

    // render targets list
    if (this.targetsList) {
      this.targetsList.innerHTML = "";
      const ids = ov.target.ovs;

      if (!ids.length) {
        const empty = document.createElement("div");
        empty.className = "small";
        empty.textContent = "(no targets)";
        this.targetsList.appendChild(empty);
      } else {
        for (const id of ids) {
          const tOv = this.overlaysM.getOverlayById(id);
          const row = document.createElement("div");
          row.className = "titem";

          const name = document.createElement("div");
          name.className = "tname";
          name.textContent = tOv ? (tOv.name ? `${tOv.name} (${tOv.id})` : tOv.id) : id;
          row.appendChild(name);

          const del = document.createElement("button");
          del.className = "danger";
          del.textContent = "Delete";
          del.addEventListener("click", () => {
            const curOv = this.overlaysM.getOverlayById(this.selOverlay.value);
            if (!curOv) return;
            this._ensureTarget(curOv);
            curOv.target.ovs = (curOv.target.ovs || []).filter(x => x !== id);
            this.syncFromSelection();
          });
          row.appendChild(del);

          this.targetsList.appendChild(row);
        }
      }
    }

    // target layer selector
    if (this.targetLayerSel) {
      const n = this.data?.page?.infoLayers?.length || 0;
      if (n > 0) {
        const val = String(ov.target.infolayerindex ?? 0);
        if (this.targetLayerSel.options.length === 0) {
          this.targetLayerSel.innerHTML = "";
          for (let i = 0; i < n; i++) {
            const opt = document.createElement("option");
            opt.value = String(i);
            opt.textContent = `target layer index: ${i}`;
            this.targetLayerSel.appendChild(opt);
          }
        }
        this.targetLayerSel.value = val;
      }
    }
  }
}
